import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageModule } from 'primeng/message';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { AuthProvider, SessionsService } from '../../services/sessions.service';
import { AntiMemLeak } from '../../../../../shared/classes/anti-mem-leak';

export enum PasswordResetType {
  FORCE_CHANGE_PASSWORD = 'force-change-password',
  FORGOT_PASSWORD = 'forgot-password',
  RESET_PASSWORD = 'reset-password',
}

@Component({
  selector: 'app-password-reset',
  standalone: true,
  imports: [
    Button,
    ReactiveFormsModule,
    DividerModule,
    FloatLabelModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    CardModule,
    FormsModule,
    RouterModule,
    MessageModule,
    SelectModule,
  ],
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.scss',
})
export class PasswordResetComponent extends AntiMemLeak implements OnInit {
  protected router: Router = inject(Router);
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected sessionService: SessionsService = inject(SessionsService);

  protected resetType: PasswordResetType = PasswordResetType.FORGOT_PASSWORD;
  protected loading: boolean = false;
  protected errorMessage: string | null = null;
  protected successMessage: string | null = null;
  protected showConfirmationForm: boolean = false;
  protected availableProviders: AuthProvider[] = [];
  protected selectedProvider: AuthProvider | null = null;

  protected formGroup: FormGroup = new FormGroup({
    provider: new FormControl('', [Validators.required]),
    username: new FormControl('', [Validators.required, Validators.email]),
    newPassword: new FormControl('', []),
    confirmPassword: new FormControl('', []),
    confirmationCode: new FormControl('', []),
  });

  protected resetTypeLabels = {
    [PasswordResetType.FORCE_CHANGE_PASSWORD]: 'Change Your Password',
    [PasswordResetType.FORGOT_PASSWORD]: 'Reset Password',
    [PasswordResetType.RESET_PASSWORD]: 'Set New Password',
  };

  async ngOnInit(): Promise<void> {
    this.availableProviders = this.sessionService
      .getAuthenticationProviders()
      .filter((provider) => provider.supportsPasswordReset !== false);

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        this.resetType = params['type'] || PasswordResetType.FORGOT_PASSWORD;
        const username = params['username'] || '';
        const providerName = params['provider'] || '';

        console.log(params);

        if (username) {
          this.formGroup.get('username')?.setValue(username);
          this.formGroup.get('username')?.disable();
        }

        if (providerName) {
          this.selectedProvider =
            this.availableProviders.find((p) => p.name.toLowerCase() === providerName.toLowerCase()) || null;
          if (this.selectedProvider) {
            this.formGroup.get('provider')?.setValue(this.selectedProvider);
            this.formGroup.get('provider')?.disable();
          }
        }

        if (this.resetType === PasswordResetType.FORCE_CHANGE_PASSWORD) {
          this.showConfirmationForm = true;
          this.updateValidators();
        }
      })
    );

    this.formGroup.addValidators(this.passwordMatchValidator());
  }

  private updateValidators() {
    if (this.showConfirmationForm) {
      this.formGroup
        .get('newPassword')
        ?.setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/),
        ]);
      this.formGroup.get('confirmPassword')?.setValidators([Validators.required]);
      this.formGroup.get('confirmationCode')?.setValidators([Validators.required]);
    } else {
      this.formGroup.get('newPassword')?.setValidators([]);
      this.formGroup.get('confirmPassword')?.setValidators([]);
      this.formGroup.get('confirmationCode')?.setValidators([]);
    }

    this.formGroup.get('newPassword')?.updateValueAndValidity();
    this.formGroup.get('confirmPassword')?.updateValueAndValidity();
    this.formGroup.get('confirmationCode')?.updateValueAndValidity();
  }

  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const formGroup = control as FormGroup;
      const password = formGroup.get('newPassword');
      const confirmPassword = formGroup.get('confirmPassword');

      if (password && confirmPassword && password.value !== confirmPassword.value) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }

      if (confirmPassword?.hasError('passwordMismatch')) {
        confirmPassword.setErrors(null);
      }

      return null;
    };
  }

  protected getTitle(): string {
    return this.resetTypeLabels[this.resetType];
  }

  protected shouldShowField(fieldName: string): boolean {
    switch (fieldName) {
      case 'confirmationCode':
        return this.showConfirmationForm;
      case 'newPassword':
      case 'confirmPassword':
        return this.showConfirmationForm || this.resetType === PasswordResetType.FORCE_CHANGE_PASSWORD;
      default:
        return true;
    }
  }

  protected async initiatePasswordReset() {
    this.errorMessage = null;
    this.successMessage = null;
    this.loading = true;

    try {
      const provider = this.getSelectedProvider();
      if (!provider) {
        throw new Error('Please select an authentication provider');
      }

      if (!provider.resetPassword) {
        throw new Error('Selected provider does not support password reset');
      }

      const username = this.formGroup.get('username')?.value;

      await provider.resetPassword(username);

      this.successMessage = 'Password reset code sent to your email. Please check your inbox and enter the code below.';
      this.showConfirmationForm = true;
      this.formGroup.get('username')?.setValue(username);
      this.formGroup.get('username')?.disable();

      this.updateValidators();
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to initiate password reset. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  protected async confirmPasswordReset() {
    this.errorMessage = null;
    this.successMessage = null;
    this.loading = true;

    try {
      const provider = this.getSelectedProvider();
      if (!provider) {
        throw new Error('Please select an authentication provider');
      }

      if (!provider.confirmResetPassword) {
        throw new Error('Selected provider does not support password reset confirmation');
      }

      const username = this.formGroup.get('username')?.value;
      const confirmationCode = this.formGroup.get('confirmationCode')?.value;
      const newPassword = this.formGroup.get('newPassword')?.value;

      await provider.confirmResetPassword(username, confirmationCode, newPassword);

      this.successMessage = 'Password has been successfully reset. You can now sign in with your new password.';

      setTimeout(() => {
        this.router.navigate(['/session/signin']);
        this.loading = false;
      }, 2000);
    } catch (error: any) {
      this.errorMessage =
        error.message || 'Failed to reset password. Please check your confirmation code and try again.';
      this.loading = false;
    }
  }

  private getSelectedProvider(): AuthProvider | null {
    if (this.selectedProvider) {
      return this.selectedProvider;
    }

    const providerFromForm = this.formGroup.get('provider')?.value;
    if (providerFromForm) {
      return providerFromForm;
    }

    if (this.availableProviders.length === 1) {
      return this.availableProviders[0];
    }

    return null;
  }

  protected onSubmit() {
    if (!this.isValid()) {
      this.markRelevantFieldsAsTouched();
      return;
    }

    if (!this.showConfirmationForm) {
      this.initiatePasswordReset();
    } else {
      this.confirmPasswordReset();
    }
  }

  private markRelevantFieldsAsTouched() {
    if (!this.showConfirmationForm) {
      this.formGroup.get('provider')?.markAsTouched();
      this.formGroup.get('username')?.markAsTouched();
    } else {
      this.formGroup.get('provider')?.markAsTouched();
      this.formGroup.get('username')?.markAsTouched();
      this.formGroup.get('confirmationCode')?.markAsTouched();
      this.formGroup.get('newPassword')?.markAsTouched();
      this.formGroup.get('confirmPassword')?.markAsTouched();
    }
  }

  protected isValid(): boolean {
    if (!this.showConfirmationForm) {
      const providerValid = this.availableProviders.length === 1 || (this.formGroup.get('provider')?.valid ?? false);
      const usernameValid = this.formGroup.get('username')?.valid ?? false;
      return providerValid && usernameValid;
    }

    return this.formGroup.valid;
  }

  protected getPasswordError(): string | null {
    const newPassword = this.formGroup.get('newPassword');
    if (newPassword?.hasError('required') && newPassword?.touched) {
      return 'Password is required';
    }
    if (newPassword?.hasError('minlength') && newPassword?.touched) {
      return 'Password must be at least 8 characters long';
    }
    if (newPassword?.hasError('pattern') && newPassword?.touched) {
      return 'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character';
    }
    return null;
  }

  protected getConfirmPasswordError(): string | null {
    const confirmPassword = this.formGroup.get('confirmPassword');
    if (confirmPassword?.hasError('required') && confirmPassword?.touched) {
      return 'Please confirm your password';
    }
    if (confirmPassword?.hasError('passwordMismatch') && confirmPassword?.touched) {
      return 'Passwords do not match';
    }
    return null;
  }

  protected goBackToSignIn() {
    this.router.navigate(['/session/signin']);
  }
}
